import React, { useState } from 'react';
import { GripVertical, Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useSortable, SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import RoundRow from './RoundRow';
import { usePatternStudio } from '../context/PatternStudioContext';

const SECTION_COLORS = [
  '#C4B8A8', '#8B9E7E', '#E8C4B8', '#B8C4E8', '#E8E4B8',
  '#D4B8E8', '#B8E8D4', '#E8B8B8',
];

export default function SectionCard({ section }) {
  const { dispatch } = usePatternStudio();
  const [collapsed, setCollapsed] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

  const sensors = useSensors(useSensor(PointerSensor));

  function handleRoundDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = section.rounds.findIndex(r => r.id === active.id);
    const newIdx = section.rounds.findIndex(r => r.id === over.id);
    dispatch({
      type: 'REORDER_ROUNDS',
      sectionId: section.id,
      rounds: arrayMove(section.rounds, oldIdx, newIdx),
    });
  }

  function addRound(type = 'round') {
    dispatch({ type: 'ADD_ROUND', sectionId: section.id, roundType: type });
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* Section header */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderLeft: `4px solid ${section.color || '#C4B8A8'}` }}
      >
        {/* Drag handle */}
        <div {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 flex-shrink-0">
          <GripVertical size={16} />
        </div>

        {/* Color dot */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowColorPicker(v => !v)}
            className="w-4 h-4 rounded-full border-2 border-white shadow-sm transition-transform hover:scale-110"
            style={{ background: section.color || '#C4B8A8' }}
          />
          {showColorPicker && (
            <div className="absolute left-0 top-7 bg-white border border-gray-200 rounded-xl shadow-xl p-2 flex flex-wrap gap-1.5 z-20" style={{ width: '120px' }}>
              {SECTION_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => { dispatch({ type: 'UPDATE_SECTION', id: section.id, changes: { color: c } }); setShowColorPicker(false); }}
                  className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                  style={{ background: c, borderColor: section.color === c ? '#6B5744' : 'transparent' }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Title */}
        <input
          value={section.title}
          onChange={e => dispatch({ type: 'UPDATE_SECTION', id: section.id, changes: { title: e.target.value } })}
          className="flex-1 font-semibold text-gray-900 text-sm bg-transparent outline-none border-b border-transparent focus:border-stone-400 min-w-0"
        />

        {/* Round count */}
        <span className="text-xs text-gray-400 flex-shrink-0">
          {section.rounds.length} {section.rounds.length === 1 ? 'round' : 'rounds'}
        </span>

        {/* Collapse */}
        <button
          onClick={() => setCollapsed(v => !v)}
          className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>

        {/* Delete section */}
        <button
          onClick={() => {
            if (section.rounds.length > 0 && !confirm(`Delete "${section.title}"? This removes all its rounds.`)) return;
            dispatch({ type: 'REMOVE_SECTION', id: section.id });
          }}
          className="p-1 text-gray-300 hover:text-red-500 transition-all flex-shrink-0"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Rounds list */}
      {!collapsed && (
        <div className="px-4 pb-3 pt-1">
          {section.rounds.length === 0 ? (
            <div className="flex items-center justify-center py-6 border-2 border-dashed border-stone-100 rounded-xl">
              <p className="text-xs text-gray-400">No rounds yet — add one below</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRoundDragEnd}>
              <SortableContext items={section.rounds.map(r => r.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1.5">
                  {section.rounds.map(round => (
                    <RoundRow
                      key={round.id}
                      round={round}
                      sectionId={section.id}
                      onUpdate={(roundId, changes) =>
                        dispatch({ type: 'UPDATE_ROUND', sectionId: section.id, roundId, changes })
                      }
                      onRemove={roundId =>
                        dispatch({ type: 'REMOVE_ROUND', sectionId: section.id, roundId })
                      }
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Add round buttons */}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => addRound('round')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-xs text-gray-600 hover:bg-stone-100 transition-all"
            >
              <Plus size={11} /> Round
            </button>
            <button
              onClick={() => addRound('row')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-xs text-gray-600 hover:bg-stone-100 transition-all"
            >
              <Plus size={11} /> Row
            </button>
            <button
              onClick={() => addRound('tip')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 hover:bg-amber-100 transition-all"
            >
              <Plus size={11} /> Tip
            </button>
            <button
              onClick={() => addRound('warning')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600 hover:bg-red-100 transition-all"
            >
              <Plus size={11} /> Warning
            </button>
            <button
              onClick={() => addRound('note')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-600 hover:bg-blue-100 transition-all"
            >
              <Plus size={11} /> Note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
